import {SingleFileModePullRequestDiffEntry} from './SingleFileModePullRequestDiffEntry'
import {memo, useEffect, useMemo} from 'react'
import {
  useDiffEntries,
  useDiffSummariesArray,
  useDiffSummaryCount,
  useProgressiveDiffActions,
  useSelectedPathDigest,
} from '../stores/selectors'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import {useScrollToAndFocus} from '../hooks/use-scroll-to-and-focus'
import {updateUrlHash} from '@github-ui/history'

export interface SingleFileDiffEntryNavigatorProps {
  filteredDiffSummaries: PullRequestFileTreeDiff[]
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  pullRequestGlobalRelayId: string
  repository: {
    id: number
    name: string
    ownerLogin: string
  }
}
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const SingleFileModeDiffEntryNavigator = memo(function SingleFileModeDiffEntryNavigator({
  filteredDiffSummaries,
  headBranchName,
  headRepoName,
  headRepoOwner,
  pullRequestGlobalRelayId,
  repository,
}: SingleFileDiffEntryNavigatorProps) {
  const diffEntriesMap = useDiffEntries()
  const diffEntries = useMemo(() => {
    {
      return Object.values(diffEntriesMap ?? {}).filter(diffEntry => diffEntry !== null)
    }
  }, [diffEntriesMap])
  const diffEntry = diffEntries[0] // Get the first diff entry
  const selectedPathDigest = useSelectedPathDigest()
  const {onScrollToAndFocusEntry, getTargetableEntriesMap} = useScrollToAndFocus()

  const diffSummariesCount = useDiffSummaryCount()
  const firstDiffSummary = useDiffSummariesArray()[0]

  const {loadPriority, startQueue} = useProgressiveDiffActions()

  useEffect(() => {
    startQueue()
  }, [startQueue])

  useEffect(() => {
    loadPriority(selectedPathDigest)
  }, [loadPriority, selectedPathDigest])

  useEffect(() => {
    if (!selectedPathDigest && firstDiffSummary?.pathDigest) {
      // If no path is selected, automatically select the first diff entry and update the URL hash
      updateUrlHash(`diff-${firstDiffSummary.pathDigest}`)
    }
  }, [firstDiffSummary?.pathDigest, selectedPathDigest])

  /*
   * Empty state for PRs with no changed files.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (filteredDiffSummaries.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  const pathDigest = selectedPathDigest || diffEntry?.pathDigest || firstDiffSummary?.pathDigest
  if (!pathDigest) return null

  return (
    <div
      ref={(node: HTMLDivElement) => {
        const map = getTargetableEntriesMap()
        if (node) {
          map.set(pathDigest, node)
        } else {
          map.delete(pathDigest)
        }
      }}
    >
      <SingleFileModePullRequestDiffEntry
        diffEntryPathDigest={pathDigest}
        diffManuallyExpanded={false}
        filteredDiffSummaries={filteredDiffSummaries}
        headBranchName={headBranchName}
        headRepoName={headRepoName}
        headRepoOwner={headRepoOwner}
        pullRequestGlobalRelayId={pullRequestGlobalRelayId}
        repository={repository}
        onScrollToAndFocusEntry={onScrollToAndFocusEntry}
      />
    </div>
  )
})
